import type { CartProductListItemDto } from '@repo/api/web/models'

import { useCartModule } from './useCartModule'

export interface SelectedEntry {
  id: number
  index: number
}

export const useCartSelection = () => {
  const { store } = useCartModule()

  const cartItems = computed(() => store.cart?.items || [])

  const availableCartItems = computed(() =>
    cartItems.value.filter(item => Boolean(item.inStock)),
  )
  const unavailableCartItems = computed(() =>
    cartItems.value.filter(item => !item?.inStock),
  )
  const availableItems = computed(() =>
    availableCartItems.value.flatMap(item =>
      Array.from({ length: item.amount || 1 }, () => item),
    ),
  )

  const selectedProductIds = ref<SelectedEntry[]>([])

  function createKey(productId: number, index: number) {
    return `${productId}_${index}`
  }

  function getAllAvailableEntries(): SelectedEntry[] {
    return availableCartItems.value.flatMap(item =>
      Array.from({ length: item.amount || 1 }, (_, index) => ({
        id: item.id!,
        index,
      })),
    )
  }

  function selectAllAvailable() {
    selectedProductIds.value = getAllAvailableEntries()
  }

  function pruneInvalidSelection() {
    const validKeys = new Set<string>()

    availableCartItems.value.forEach((item) => {
      for (let i = 0; i < (item.amount || 1); i++) {
        validKeys.add(createKey(item.id!, i))
      }
    })

    selectedProductIds.value = selectedProductIds.value.filter(selectedItem =>
      validKeys.has(createKey(selectedItem.id, selectedItem.index)),
    )
  }

  selectAllAvailable()

  watch(cartItems, pruneInvalidSelection)

  const selectedItems = computed(() => {
    const itemMap = new Map<
      number,
      { item: CartProductListItemDto, indices: Set<number> }
    >()

    for (const entry of selectedProductIds.value) {
      if (!itemMap.has(entry.id)) {
        const item = availableItems.value.find(i => i.id === entry.id)
        if (item) itemMap.set(entry.id, { item, indices: new Set() })
      }

      itemMap.get(entry.id)?.indices.add(entry.index)
    }

    return Array.from(itemMap.values()).map(({ item, indices }) => ({
      ...item,
      amount: indices.size,
    }))
  })

  const allSelected = computed(
    () =>
      availableItems.value.length > 0
      && selectedProductIds.value.length >= availableItems.value.length,
  )

  const canCheckout = computed(() => selectedItems.value.length > 0)

  function toggleSelectAll() {
    if (allSelected.value) {
      selectedProductIds.value = []
    } else {
      selectAllAvailable()
    }
  }

  function handleSelectedChange(payload: SelectedEntry & { value: boolean }) {
    if (payload.value) {
      const item = cartItems.value.find(i => i.id === payload.id)
      if (!item || (item.inStock ?? 0) < 1) return
      const exists = selectedProductIds.value.some(
        selectedItem =>
          selectedItem.id === payload.id && selectedItem.index === payload.index,
      )
      if (!exists) {
        selectedProductIds.value.push({ id: payload.id, index: payload.index })
      }
    } else {
      selectedProductIds.value = selectedProductIds.value.filter(
        s => !(s.id === payload.id && s.index === payload.index),
      )
    }
  }

  return {
    cartItems,
    availableCartItems,
    unavailableCartItems,
    availableItems,
    selectedProductIds,
    selectedItems,
    allSelected,
    canCheckout,
    toggleSelectAll,
    handleSelectedChange,
  }
}
